import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { pmApi } from '../../api/pm.js';

const TEAM_CAPACITY_DEFAULT = 30; // story points per sprint, configurable per team

export default function Sprints() {
  const { key } = useParams();
  const [project, setProject] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [backlog, setBacklog] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => { pmApi.getProject(key).then((d) => setProject(d?.project)); }, [key]);

  useEffect(() => {
    if (!project) return;
    refresh();
    // eslint-disable-next-line
  }, [project]);

  async function refresh() {
    const [s, i] = await Promise.all([pmApi.sprints(project.id), pmApi.issues({ project_id: project.id })]);
    setSprints(s?.items || []);
    setBacklog(i?.items || []);
    if (!activeId && s?.items?.length) setActiveId(s.items[0].id);
  }

  useEffect(() => {
    if (activeId) pmApi.getSprint(activeId).then((d) => setActive(d));
  }, [activeId]);

  const sprintIds = useMemo(() => new Set((active?.issues || []).map((i) => i.id)), [active]);
  const backlogOnly = useMemo(() => backlog.filter((i) => !sprintIds.has(i.id)), [backlog, sprintIds]);
  const sprintPoints = useMemo(() => (active?.capacity?.totalPoints || 0), [active]);

  async function add(issueId) {
    await pmApi.addToSprint(activeId, [issueId]);
    refresh();
    pmApi.getSprint(activeId).then(setActive);
  }
  async function remove(issueId) {
    await pmApi.removeFromSprint(activeId, issueId);
    refresh();
    pmApi.getSprint(activeId).then(setActive);
  }

  async function start() {
    setError(null);
    try { await pmApi.startSprint(activeId); pmApi.getSprint(activeId).then(setActive); refresh(); }
    catch (err) { setError(err.response?.data?.message || 'Failed'); }
  }
  async function complete() {
    if (!window.confirm('Complete this sprint? Open issues will return to the backlog.')) return;
    try { await pmApi.completeSprint(activeId); pmApi.getSprint(activeId).then(setActive); refresh(); }
    catch (err) { setError(err.response?.data?.message || 'Failed'); }
  }

  async function createSprint(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const created = await pmApi.createSprint({
      projectId: project.id,
      name: fd.get('name'),
      goal: fd.get('goal') || null,
      startDate: fd.get('startDate') || null,
      endDate: fd.get('endDate') || null,
    });
    setActiveId(created.sprint.id);
    setShowForm(false);
    e.currentTarget.reset();
    refresh();
  }

  if (!project) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <h1>{project.name} · Sprints</h1>
        <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Sprint'}</button>
      </header>

      {error && <div className="alert error">{error}</div>}
      {showForm && (
        <form onSubmit={createSprint} className="card form-card grid-form">
          <label>Name<input name="name" required /></label>
          <label>Goal<input name="goal" /></label>
          <label>Start<input name="startDate" type="date" /></label>
          <label>End<input name="endDate" type="date" /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}

      <div className="card form-card">
        <label>
          Sprint
          <select value={activeId || ''} onChange={(e) => setActiveId(e.target.value)}>
            {sprints.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.status}</option>)}
            {sprints.length === 0 && <option>No sprints</option>}
          </select>
        </label>
      </div>

      {active?.sprint && (
        <>
          <div className="card capacity-card">
            <div>
              <h3>{active.sprint.name}</h3>
              {active.sprint.goal && <p className="muted">{active.sprint.goal}</p>}
              <p className="muted small">{active.sprint.start_date || '—'} → {active.sprint.end_date || '—'}</p>
              <span className={`chip status-${active.sprint.status === 'active' ? 'approved' : active.sprint.status === 'planning' ? 'pending' : 'cancelled'}`}>{active.sprint.status}</span>
            </div>
            <CapacityBar points={sprintPoints} capacity={TEAM_CAPACITY_DEFAULT} />
            <div className="actions">
              {active.sprint.status === 'planning' && <button className="btn primary" onClick={start}>Start sprint</button>}
              {active.sprint.status === 'active' && <button className="btn primary" onClick={complete}>Complete sprint</button>}
            </div>
          </div>

          <div className="grid-2">
            <div className="card">
              <h3>Backlog ({backlogOnly.length})</h3>
              <ul className="issue-list">
                {backlogOnly.map((i) => (
                  <li key={i.id}>
                    <Link to={`/pm/issues/${i.full_key}`}><code>{i.full_key}</code> {i.title}</Link>
                    <span className="muted small">{i.story_points || 0} pts</span>
                    <button className="btn ghost small" onClick={() => add(i.id)}>+</button>
                  </li>
                ))}
                {backlogOnly.length === 0 && <li className="muted">All issues are in sprints.</li>}
              </ul>
            </div>
            <div className="card">
              <h3>Sprint scope ({active.issues.length})</h3>
              <ul className="issue-list">
                {active.issues.map((i) => (
                  <li key={i.id}>
                    <Link to={`/pm/issues/${i.project_key}-${i.issue_no}`}><code>{i.project_key}-{i.issue_no}</code> {i.title}</Link>
                    <span className="muted small">{i.story_points || 0} pts</span>
                    {active.sprint.status !== 'completed' && (
                      <button className="btn ghost small" onClick={() => remove(i.id)}>−</button>
                    )}
                  </li>
                ))}
                {active.issues.length === 0 && <li className="muted">Empty — drop in some issues.</li>}
              </ul>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function CapacityBar({ points, capacity }) {
  const pct = Math.min(100, capacity ? (points / capacity) * 100 : 0);
  const over = points > capacity;
  return (
    <div className="capacity-bar">
      <div className="cap-meta">
        <strong>{points}</strong> / {capacity} pts
        {over && <span className="chip status-rejected">over capacity</span>}
      </div>
      <div className="cap-track">
        <div className="cap-fill" style={{ width: `${pct}%`, background: over ? '#dc2626' : '#2563eb' }} />
      </div>
    </div>
  );
}
